/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { useEffect, useRef, useState } from "react";
import { getDriverMapPoints } from "../core/_requests";

interface LatLng {
  lat: number;
  lng: number;
}

interface OrderProps {
  orderId: string;
  onClose: () => void; // Function to close modal
}

const LiveTrackingMap: React.FC<OrderProps> = ({ orderId, onClose }) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const driverMarkerRef = useRef<google.maps.Marker | null>(null);
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);

  const [driver, setDriver] = useState<LatLng | null>(null);
  const [destination, setDestination] = useState<LatLng | null>(null);
  const [mode, setMode] = useState<string>("");

  // 🔄 Fetch tracking points
  useEffect(() => {
    const fetchTrackingPoints = async () => {
      try {
        const res = await getDriverMapPoints(orderId);
        if (res.status && res.data) {
          setDriver({
            lat: parseFloat(res.data.driver.latitude),
            lng: parseFloat(res.data.driver.longitude),
          });
          setDestination({
            lat: parseFloat(res.data.destination.latitude),
            lng: parseFloat(res.data.destination.longitude),
          });
          setMode(res.data.mode);
        }
      } catch (err) {
        console.error("Error fetching tracking points", err);
      }
    };

    fetchTrackingPoints();
    const interval = setInterval(fetchTrackingPoints, 90000);
    return () => clearInterval(interval);
  }, [orderId]);

  // Initialize Google Map
  useEffect(() => {
    if (!window.google || mapRef.current) return;

    const map = new window.google.maps.Map(
      document.getElementById("trackingMap") as HTMLElement,
      { center: { lat: 0, lng: 0 }, zoom: 14, mapTypeId: "roadmap" }
    );

    directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
      map,
      suppressMarkers: true,
      polylineOptions: { strokeColor: "blue", strokeWeight: 5 },
    });

    mapRef.current = map;
  }, []);

  // Place Destination Marker (Chef OR User)
  useEffect(() => {
    if (!destination || !window.google || !mapRef.current) return;

    new window.google.maps.Marker({
      position: destination,
      map: mapRef.current,
      label: mode === "driver_to_chef" ? "C" : "U", // 👈 Chef = C, User = U
    });

    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(destination);
    if (driver) bounds.extend(driver);
    mapRef.current.fitBounds(bounds);
  }, [destination, mode]);

  // Update Driver Marker & Route
  useEffect(() => {
    if (!driver || !destination || !window.google || !mapRef.current) return;

    if (!driverMarkerRef.current) {
      driverMarkerRef.current = new window.google.maps.Marker({
        position: driver,
        map: mapRef.current,
        icon: {
          url: "/assets/images/gps-navigation.png",
          scaledSize: new window.google.maps.Size(40, 40),
        },
      });
    } else {
      driverMarkerRef.current.setPosition(driver);
    }

    mapRef.current.panTo(driver);

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      { origin: driver, destination, travelMode: window.google.maps.TravelMode.DRIVING },
      (result, status) => {
        if (status === "OK" && result) {
          directionsRendererRef.current?.setDirections(result);
        }
      }
    );
  }, [driver, destination]);

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        {/* Header */}
        <div className="modal-header">
             <img
      src="/assets/images/logo.svg"
      alt="Frayo Logo"
      className="modal-logo"
    />
          <h3> Frayo Order Tracking </h3>
          
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Map */}
        <div
          id="trackingMap"
          style={{ width: "100%", height: "400px", borderRadius: "10px" }}
        />
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0; left: 0;
          width: 100%; height: 100%;
          background: rgba(0,0,0,0.5);
          display: flex; justify-content: center; align-items: center;
          z-index: 1000;
        }
        .modal-content {
          background: #fff;
          border-radius: 10px;
          width: 600px; max-width: 90%;
          padding: 0; overflow: hidden;
        }
        .modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 10px 20px;
          background: #f5f5f5; border-bottom: 1px solid #ddd;
        }
        .modal-header h3 { margin: 0; font-size: 18px; }
        .modal-close {
          background: transparent; border: none; font-size: 24px; cursor: pointer;
        }
      `}</style>
      <style>{`
  .modal-overlay {
    position: fixed;
    top: 0; 
    left: 0;
    width: 100%; 
    height: 100%;
    background: rgba(0,0,0,0.5);
    display: flex; 
    justify-content: center; 
    align-items: center;
    z-index: 1000;
  }
  .modal-content {
    background: #fff;
    border-radius: 10px;
    width: 600px; 
    max-width: 90%;
    padding: 0; 
    overflow: hidden;
  }
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 20px;
    background: #f5f5f5; 
    border-bottom: 1px solid #ddd;
  }
  .modal-title {
    display: flex;
    align-items: center;
    gap: 10px; /* space between logo and text */
  }
  .modal-logo {
    width: 30px;   /* adjust as needed */
    height: 30px;  /* maintain aspect ratio */
  }
  .modal-header h3, .modal-title span {
    margin: 0; 
    font-size: 18px;
  }
  .modal-close {
    background: transparent; 
    border: none; 
    font-size: 24px; 
    cursor: pointer;
  }
`}</style>

    </div>
  );
};

export default LiveTrackingMap;
